import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, Search, ArrowRight } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import blogService from '../../../services/blogService';
import LoadingSpinner from '../../common/LoadingSpinner';
import ErrorMessage from '../../common/ErrorMessage';

const cardVariants = {
  offscreen: { opacity: 0, y: 30 },
  onscreen: { 
    opacity: 1, y: 0,
    transition: { type: 'spring', stiffness: 200, damping: 24 }
  }
};

const BlogList = () => {
  const { isDark } = useTheme();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [postsPerPage] = useState(6);

  useEffect(() => {
    fetchBlogData();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [currentPage, selectedCategory, selectedTag, searchTerm]);

  const fetchBlogData = async () => {
    try {
      const [categoriesRes, tagsRes] = await Promise.all([
        blogService.getCategories(),
        blogService.getTags()
      ]);
      setCategories(categoriesRes.data.categories);
      setTags(tagsRes.data.tags);
    } catch (err) {
      console.error('Error fetching blog data:', err);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const filters = {
        status: 'published',
        limit: postsPerPage,
        offset: (currentPage - 1) * postsPerPage,
      };
      if (selectedCategory) filters.category_id = parseInt(selectedCategory);
      if (selectedTag) filters.tag_id = parseInt(selectedTag);
      if (searchTerm && searchTerm.trim().length >= 2) {
        filters.search = searchTerm.trim();
      }

      const response = await blogService.getPosts(filters);
      setPosts(response.data.posts);
      setTotalPages(Math.ceil(response.data.total / postsPerPage));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPosts();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedTag('');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchTerm || selectedCategory || selectedTag;

  if (loading && posts.length === 0) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchPosts} />;
  }

  return (
    <div className="max-w-4xl mx-auto py-8 pb-16">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-10"
      >
        <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-neutral-100' : 'text-neutral-900'}`}>
          Blog
        </h1>
        <p className={`text-sm ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
          Notes, write-ups, and things I've learned along the way.
        </p>
      </motion.div>

      {/* Search & Filters */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className={`mb-8 p-4 rounded-xl border-gradient ${isDark ? 'glass-futuristic' : 'glass-futuristic-light'}`}
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search posts..."
              className={`w-full pl-9 pr-3 py-2 rounded-lg text-sm border transition-colors ${
                isDark
                  ? 'bg-neutral-900/60 border-neutral-800 text-neutral-200 placeholder-neutral-500 focus:border-accent-500/40'
                  : 'bg-white border-neutral-200 text-neutral-800 placeholder-neutral-400 focus:border-accent-500/50'
              } focus:outline-none`}
            />
          </div>

          {categories.length > 0 && (
            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
              className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                isDark
                  ? 'bg-neutral-900/60 border-neutral-800 text-neutral-200 focus:border-accent-500/40'
                  : 'bg-white border-neutral-200 text-neutral-800 focus:border-accent-500/50'
              } focus:outline-none`}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          )}

          {tags.length > 0 && (
            <select
              value={selectedTag}
              onChange={(e) => { setSelectedTag(e.target.value); setCurrentPage(1); }}
              className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                isDark
                  ? 'bg-neutral-900/60 border-neutral-800 text-neutral-200 focus:border-accent-500/40'
                  : 'bg-white border-neutral-200 text-neutral-800 focus:border-accent-500/50'
              } focus:outline-none`}
            >
              <option value="">All Tags</option>
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>{tag.name}</option>
              ))}
            </select>
          )}
        </form>

        {hasActiveFilters && (
          <div className="mt-3 flex items-center gap-2">
            <span className={`text-xs ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>Filtered</span>
            <button onClick={clearFilters} className="text-xs text-accent-500 hover:text-accent-400 transition-colors">
              Clear all
            </button>
          </div>
        )}
      </motion.div>

      {/* Posts list */}
      <div className="space-y-5">
        {posts.map((post) => (
          <motion.article
            key={post.id}
            variants={cardVariants}
            initial="offscreen"
            whileInView="onscreen"
            viewport={{ once: true, margin: '-40px' }}
          >
            <Link
              to={`/blog/${post.slug}`}
              className={`
                group block p-5 rounded-xl transition-all duration-300 border-gradient
                ${isDark
                  ? 'glass-futuristic hover:shadow-lg hover:shadow-accent-500/10'
                  : 'glass-futuristic-light hover:shadow-lg hover:shadow-accent-500/10'}
              `}
            >
              <div className="flex flex-col sm:flex-row gap-4">
                {post.featured_image && (
                  <div className="sm:w-40 sm:h-28 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <h2 className={`text-lg font-semibold mb-1.5 group-hover:text-accent-500 transition-colors ${isDark ? 'text-neutral-100' : 'text-neutral-900'}`}>
                    {post.title}
                  </h2>

                  <p className={`text-sm mb-3 line-clamp-2 leading-relaxed ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
                    {post.excerpt || post.content?.substring(0, 150) + '...'}
                  </p>

                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.published_at)}
                    </span>
                    {post.reading_time && (
                      <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
                        <Clock className="w-3 h-3" />
                        {post.reading_time} min
                      </span>
                    )}
                    <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      <User className="w-3 h-3" />
                      {post.author?.name || 'Amir Akrami'}
                    </span>

                    {post.tags?.length > 0 && (
                      <div className="flex gap-1.5 ml-auto">
                        {post.tags.slice(0, 3).map((tag) => (
                          <span key={tag.id} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-accent-500/10 text-accent-400' : 'bg-accent-50 text-accent-600'}`}>
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="hidden sm:flex items-center">
                  <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isDark ? 'text-neutral-600' : 'text-neutral-300'}`} />
                </div>
              </div>
            </Link>
          </motion.article>
        ))}
      </div>

      {/* Empty state */}
      {posts.length === 0 && !loading && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`text-center py-16 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
          <p className="text-sm mb-3">No posts found.</p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-sm text-accent-500 hover:text-accent-400 transition-colors">
              Clear filters
            </button>
          )}
        </motion.div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-10 gap-1.5">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              currentPage === 1
                ? 'opacity-30 cursor-not-allowed'
                : isDark ? 'text-neutral-400 hover:bg-neutral-800' : 'text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`w-8 h-8 rounded-lg text-sm transition-colors ${
                currentPage === page
                  ? 'bg-accent-500 text-white'
                  : isDark ? 'text-neutral-400 hover:bg-neutral-800' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              currentPage === totalPages
                ? 'opacity-30 cursor-not-allowed'
                : isDark ? 'text-neutral-400 hover:bg-neutral-800' : 'text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BlogList;
